import {
  asMedicationStatementId,
  DOSE_FREQUENCIES,
  DOSE_UNITS,
  MEDICATION_ROUTES,
  type MedicationStatementId,
} from "@repo/domain";
import { z } from "zod";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientRefSchema } from "./patient.schemas";
import { codeableConceptSchema } from "./schedule.schemas";

const medicationStatusSchema = z.enum([
  "active",
  "completed",
  "entered-in-error",
  "intended",
  "stopped",
  "on-hold",
  "unknown",
  "not-taken",
]);
const periodSchema = z.object({ start: z.string().optional(), end: z.string().optional() });
const medicationDosageSchema = z.object({
  amount: z.object({ value: z.number().positive(), unit: z.enum(DOSE_UNITS) }).optional(),
  frequency: z.enum(DOSE_FREQUENCIES).optional(),
  route: z.enum(MEDICATION_ROUTES).optional(),
  asNeeded: z.boolean().optional(),
});

export const medicationStatementIdSchema: z.ZodType<MedicationStatementId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asMedicationStatementId);

export const recordMedicationInputSchema = z.object({
  id: newEntityIdSchema,
  patient: patientRefSchema,
  medication: codeableConceptSchema,
  status: medicationStatusSchema.default("active"),
  effectiveDateTime: z.string().optional(),
  effectivePeriod: periodSchema.optional(),
  dosageText: z.string().optional(),
  dosage: medicationDosageSchema.optional(),
  dateAsserted: z.string().optional(),
});
export type RecordMedicationInput = z.infer<typeof recordMedicationInputSchema>;

export const medicationIdInputSchema = z.object({ medicationStatementId: medicationStatementIdSchema });
export type MedicationIdInput = z.infer<typeof medicationIdInputSchema>;

export const updateMedicationInputSchema = z.object({
  medicationStatementId: medicationStatementIdSchema,
  dosageText: z.string().optional(),
  dosage: medicationDosageSchema.optional(),
  effectiveDateTime: z.string().optional(),
  effectivePeriod: periodSchema.optional(),
});
export type UpdateMedicationInput = z.infer<typeof updateMedicationInputSchema>;
